import type { Invite, InviteForm, SendInviteError } from '@cobliteam/common-types';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { Configuration, IRequest, IRequestResponse } from '../../request/Types';
import { convertInviteFormToIInviteForm, convertIPendingInviteToInvite } from './InviteTransformer';
import type { ICreatedUser, IEnrollmentFormInfo, IInviteAcceptForm, IInviteForm, IOccupation, IPendingInvite } from './Types';

export class Invites {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getInviteInfo = (token: string): Promise<IEnrollmentFormInfo> => {
    const requestData: IRequest = {
      method: HttpMethod.GET,
      endpoint: `/account/invites/${token}`,
    };

    return this.requester
      .request(requestData)
      .then((response) => {
        const enrollmentFormInfo: IEnrollmentFormInfo = {
          invite: response.data.invite,
          occupations: this.mapOccupations(response.data.occupations),
        };
        return enrollmentFormInfo;
      })
      .catch((rawError) => {
        const error: IRequestResponse = {
          statusCode: rawError.response.status,
          body: rawError.response.data,
        };
        throw new Error(JSON.stringify(error));
      });
  };

  public acceptInvite = (acceptForm: IInviteAcceptForm, token: string): Promise<ICreatedUser> => {
    const requestData: IRequest = {
      body: JSON.stringify(acceptForm),
      method: HttpMethod.POST,
      endpoint: `/account/invites/${token}/accept`,
    };

    return this.requester
      .request(requestData)
      .then((response) => {
        const responseData = response.data;
        const createdUser: ICreatedUser = {
          id: responseData.created_user.id,
          fullName: responseData.created_user.full_name,
        };
        return createdUser;
      })
      .catch((err) => {
        throw err.response;
      });
  };

  public getPendingInvites = (): Promise<Invite[]> => {
    const requestData: IRequest = {
      method: HttpMethod.GET,
      endpoint: '/account/invites',
    };
    return this.requester
      .request(requestData)
      .then((response) => {
        const pendingInvites: IPendingInvite[] = response.data.pending_invites;
        return pendingInvites.map(convertIPendingInviteToInvite);
      })
      .catch((err) => {
        throw err.response;
      });
  };

  public sendInvites = (invites: InviteForm[]): Promise<void> => {
    const apiInviteForms: IInviteForm[] = invites.map(convertInviteFormToIInviteForm);
    const requestData: IRequest = {
      body: JSON.stringify(apiInviteForms),
      method: HttpMethod.POST,
      endpoint: '/account/invites',
    };

    return this.requester
      .request(requestData)
      .then(() => undefined)
      .catch((error) => {
        const { response } = error;
        const sendInviteError: SendInviteError = {
          status: response.status,
          errors: response.data.errors,
        };
        throw new Error(JSON.stringify(sendInviteError));
      });
  };

  public deleteInvite = (token: string): Promise<void> => {
    const requestData: IRequest = {
      method: HttpMethod.DELETE,
      endpoint: `/account/invites/${token}`,
    };

    return this.requester
      .request(requestData)
      .then(() => undefined)
      .catch((error) => {
        throw error.response;
      });
  };

  private mapOccupations = (occupations: string[]): IOccupation[] => {
    const occupationsMap: { [key: string]: string } = {
      client: 'Cliente',
      management: 'Direção',
      fleet_management: 'Gestão de Frotas',
      logistics: 'Logística',
      operations: 'Operação',
      others: 'Outros',
    };

    return occupations.map((occupation) => {
      return {
        key: occupation,
        value: occupationsMap[occupation],
      };
    });
  };
}

export default Invites;
